<template>
    <div class="dashboard-kpi">
        <div class="dashboard-kpi-tile"
            v-for="(tile, index) in tiles"
            :key="tile.key"
            :style="{'--kpi-accent': tile.color, '--kpi-delay': `${index * 70}ms`}">
            <div class="dashboard-kpi-header">
                <span class="dashboard-kpi-label">{{tile.label}}</span>

                <span class="dashboard-kpi-header-side">
                    <span v-if="!loading"
                        class="dashboard-kpi-variation"
                        :class="variationClass(tile.kpi.variation)">
                        <template v-if="tile.kpi.variation === null">sem base</template>
                        <template v-else>{{variationArrow(tile.kpi.variation)}} {{formatVariation(tile.kpi.variation)}}</template>
                    </span>

                    <KpiPeriodHelpCmp v-if="!loading"
                        :id="`dashboard-kpi-help-${tile.key}`"
                        :label="tile.label"
                        :formula="tile.formula"
                        :color="tile.color"
                        :is-money="tile.isMoney"
                        :kpi="tile.kpi"
                        :current-range="filter.date"
                        :previous-range="previousRange" />
                </span>
            </div>

            <div class="dashboard-kpi-value" v-if="!loading">
                <template v-if="tile.isMoney">{{tile.kpi.value | currency}}</template>
                <template v-else>{{formatNumber(tile.kpi.value)}}</template>
            </div>
            <div class="dashboard-kpi-skeleton" v-else></div>

            <div class="dashboard-kpi-footer">
                <span class="dashboard-kpi-caption">
                    <template v-if="previousRange">vs {{previousPeriodShort}}</template>
                    <template v-else>vs período anterior</template>
                </span>
            </div>

            <div class="dashboard-kpi-sparkline-container">
                <div class="dashboard-kpi-sparkline">
                    <highcharts :options="tile.sparkline" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import dashboardService from '@/dashboard/services/DashboardService'
import { DashboardBaseFilterModel } from '@/dashboard/models/DashboardBaseFilterModel'
import { buildKpis, cloneFilterWith, previousPeriod } from '@/dashboard/normalizers/DashboardKpiNormalizer'
import { chartColors } from '@/utils/constants/ChartTheme'
import { format } from 'date-fns'
import KpiPeriodHelpCmp from '@/dashboard/components/KpiPeriodHelpCmp'

const TILES = [
    {
        key: 'money',
        label: 'Receita',
        color: chartColors[0],
        isMoney: true,
        formula: 'Soma do valor de vendas de cada período.',
    },
    {
        key: 'sales',
        label: 'Pedidos',
        color: chartColors[3],
        isMoney: false,
        formula: 'Quantidade de pedidos de cada período.',
    },
    {
        key: 'ticket',
        label: 'Ticket médio',
        color: chartColors[1],
        isMoney: true,
        formula: 'Receita dividida pelo número de pedidos de cada período (não é a média dos tickets diários).',
    },
]

export default {
    name: 'DashboardKpiCmp',
    components: {
        KpiPeriodHelpCmp,
    },
    data() {
        return {
            loading: true,
            filter: new DashboardBaseFilterModel(),
            previousRange: null,
            kpis: buildKpis([], [], [], []),
        }
    },
    computed: {
        tiles() {
            return TILES.map((t) => {
                const kpi = this.kpis[t.key]

                return {
                    ...t,
                    kpi,
                    sparkline: this.sparklineOptions(t.color, kpi.trend),
                }
            })
        },
        previousPeriodShort() {
            if (!this.previousRange) {
                return ''
            }

            return `${format(this.previousRange.start, 'dd/MM')} a ${format(this.previousRange.end, 'dd/MM')}`
        },
    },
    methods: {
        async drawGraphic(filter) {
            this.loading = true

            try {
                const current = cloneFilterWith(filter)
                const previousRange = previousPeriod(current.date)
                const previous = cloneFilterWith(filter, {date: previousRange})

                const [moneyNow, salesNow, moneyPrev, salesPrev] = await Promise.all([
                    dashboardService.getAmountOfMoneyEarned(current),
                    dashboardService.getAmountOfSales(current),
                    dashboardService.getAmountOfMoneyEarned(previous),
                    dashboardService.getAmountOfSales(previous),
                ])

                this.filter = current
                this.previousRange = previousRange
                this.kpis = buildKpis(moneyNow, salesNow, moneyPrev, salesPrev)
            } finally {
                this.loading = false
            }
        },
        variationClass(value) {
            if (value === null || value === 0) {
                return 'is-neutral'
            }

            return value > 0 ? 'is-up' : 'is-down'
        },
        variationArrow(value) {
            if (value === 0) {
                return '='
            }

            return value > 0 ? '↑' : '↓'
        },
        formatVariation(value) {
            return `${Math.abs(value).toLocaleString('pt-BR', {maximumFractionDigits: 1})}%`
        },
        formatNumber(value) {
            return Number(value || 0).toLocaleString('pt-BR')
        },
        sparklineOptions(color, trend) {
            return {
                chart: {
                    type: 'areaspline',
                    height: 48,
                    margin: [2, 0, 2, 0],
                    backgroundColor: 'transparent',
                    animation: false,
                },
                title: {text: ''},
                credits: {enabled: false},
                legend: {enabled: false},
                tooltip: {enabled: false},
                xAxis: {visible: false, crosshair: false},
                yAxis: {visible: false, min: 0},
                plotOptions: {
                    areaspline: {
                        lineWidth: 2,
                        color,
                        fillColor: {
                            linearGradient: {x1: 0, y1: 0, x2: 0, y2: 1},
                            stops: [
                                [0, `${color}40`],
                                [1, `${color}00`],
                            ],
                        },
                        marker: {enabled: false},
                        states: {hover: {enabled: false}},
                        enableMouseTracking: false,
                    },
                },
                series: [{data: trend}],
            }
        },
    },
    async mounted() {
        await this.drawGraphic(this.filter)
    },
}
</script>

<style lang="scss" scoped>
.dashboard-kpi {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;

    @media (max-width: 767px) {
        grid-template-columns: 1fr;
    }
}

.dashboard-kpi-tile {
    position: relative;
    padding: 16px 18px 0;
    background-color: var(--surface-1);
    border: 1px solid var(--border-subtle);
    border-radius: 12px;
    box-shadow: 0 1px 2px color-mix(in srgb, var(--shadow-ink) 4%, transparent), 0 1px 3px color-mix(in srgb, var(--shadow-ink) 3%, transparent);
    animation: dashboard-kpi-in .45s cubic-bezier(.2, .7, .2, 1) both;
    animation-delay: var(--kpi-delay);

    // O popover de ajuda precisa ficar acima dos tiles vizinhos.
    &:hover,
    &:focus-within {
        z-index: 5;
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 18px;
        width: 28px;
        height: 3px;
        border-radius: 0 0 3px 3px;
        background-color: var(--kpi-accent);
    }
}

.dashboard-kpi-header-side {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.dashboard-kpi-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.dashboard-kpi-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--text-faint);
}

.dashboard-kpi-variation {
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;

    &.is-up {
        color: var(--success-fg);
        background-color: var(--success-bg);
    }

    &.is-down {
        color: var(--danger-fg);
        background-color: var(--danger-bg);
    }

    &.is-neutral {
        color: var(--text-muted);
        background-color: var(--surface-3);
    }
}

.dashboard-kpi-value {
    margin-top: 6px;
    font-size: 28px;
    font-weight: 700;
    line-height: 1.15;
    color: var(--text-strong);
    font-variant-numeric: tabular-nums;
    letter-spacing: -.01em;
}

.dashboard-kpi-skeleton {
    margin-top: 10px;
    width: 60%;
    height: 26px;
    border-radius: 6px;
    background: linear-gradient(90deg, var(--surface-3) 0%, var(--surface-3) 50%, var(--surface-3) 100%);
    background-size: 200% 100%;
    animation: dashboard-kpi-shimmer 1.2s linear infinite;
}

.dashboard-kpi-footer {
    margin-top: 2px;
}

.dashboard-kpi-caption {
    font-size: 12px;
    color: var(--text-faint);
}

.dashboard-kpi-sparkline-container {
    margin: 4px -18px 0;
    overflow: hidden;
    border-radius: 0 0 11px 11px;
}

@keyframes dashboard-kpi-in {
    from {
        opacity: 0;
        transform: translateY(6px);
    }
    to {
        opacity: 1;
        transform: none;
    }
}

@keyframes dashboard-kpi-shimmer {
    from {
        background-position: 200% 0;
    }
    to {
        background-position: -200% 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .dashboard-kpi-tile,
    .dashboard-kpi-skeleton {
        animation: none;
    }
}
</style>
